<template>
    <LazyHydration :hydrate="hydrateOnIdle()">
        <HeadMeta :prepareGoogleAds="prepareGoogleAds" />
    </LazyHydration>
    <template v-if="pageStatus === 200 || manuallyHandledError">
        <component
            :is="isLayoutVisible ? Layout : 'div'"
            v-if="noLazyHydration"
        >
            <RouterView />
        </component>
        <LazyHydration
            v-else
            :hydrate="hydrateOnIdle()"
        >
            <component :is="isLayoutVisible ? Layout : 'div'">
                <RouterView />
            </component>
        </LazyHydration>
    </template>
    <Layout v-else>
        <ErrorPage
            v-if="pageStatus >= 400 && pageStatus < 500"
            :type="404"
        />
        <ErrorPage
            v-else-if="pageStatus >= 500"
            :type="500"
        />
    </Layout>
    <ModalSignIn />
    <ModalPayment />
    <template v-if="!appStore.isOppoWL">
        <ModalInstallGooglePlay />
        <ModalPlayOnApp />
    </template>
    <ModalAdDisabled />
    <DebugDialog />
    <PageLoadBar />
</template>
<script lang="ts" setup>
// необходимый для SSR и правильных статусов ответов враппер
import { storeToRefs } from 'pinia'
import {
    computed,
    hydrateOnIdle,
    onBeforeMount,
    onMounted,
    onServerPrefetch,
    onUnmounted,
    ref,
    useSSRContext,
    watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'

import HeadMeta from '@/components/HeadMeta/HeadMeta.vue'
import LazyHydration from '@/components/LazyHydration.vue'
import { DebugDialog } from '@/components/Modals/async/DebugDialog'
import { ModalAdDisabled } from '@/components/Modals/async/ModalAdDisabled'
import { ModalInstallGooglePlay } from '@/components/Modals/async/ModalInstallGooglePlay'
import { ModalPayment } from '@/components/Modals/async/ModalPayment'
import { ModalPlayOnApp } from '@/components/Modals/async/ModalPlayOnApp'
import { ModalSignIn } from '@/components/Modals/async/ModalSignIn'
import PageLoadBar from '@/components/PageLoadBar.vue'
import { useGoogleProvidersEnabled } from '@/composables/use-google-providers-enabled'
import Layout from '@/layouts/Layout.vue'
import ErrorPage from '@/pages/Error/ErrorPage.vue'
import { ROUTE_NAMES, ROUTES_WITH_GAME_PARAM } from '@/router/constants'
import { CLIENT_QUERY, PG_QUERY_END, PG_QUERY_START, SSR_QUERY } from '@/router/query-params'
import { useAdBlockStore } from '@/store/ad-block-store'
import { useAdsStore } from '@/store/ads-store'
import { useAppStore } from '@/store/app-store'
import { useClientAppStore } from '@/store/client-app-store'
import { useGoogleAuthStore } from '@/store/google-auth-store'
import { useGlobalInterfaceEvents, useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'
import type { PlatformType, RenderCacheContext } from '@/types'
import {
    getAUID,
    getLandingClidFromCookies,
    getPlatformFromCookies,
    getRankingParamsFromCookies,
    getUACFromCookies30d,
    updateRankingCookies,
} from '@/utils/cookie-parser'
import { sha256hex } from '@/utils/crypto'
import { parseLandingAttributes } from '@/utils/landing-attributes'
import { parseDevice, resolvePlatform } from '@/utils/parse-device'
import { resolveClid } from '@/utils/resolve-clid'

import { useBrowserRumTracking } from './composables/use-browser-rum-tracking'
import { TargetingMapping } from './modules/adv/targeting-mapping'
import { useLayoutStore } from './store/layout-store'
import { useCtrStore } from './store/logger/ctr-store'
import { useModalInstallGooglePlayStore } from './store/modal-install-google-play-store'
import { useModalPlayOnAppStore } from './store/modal-play-on-app-store'
import { useNativeAppStore } from './store/native-app-store'
import { useNotificationsQueue } from './store/notification-que/notifications-queue'
import { usePushSubscriptionStore } from './store/push-subscribe/store'
import { usePWAInstallStore } from './store/pwa-install/store'
import { installVueConsoleHandlers } from './utils/vue-console-handler'

useGlobalInterfaceEvents()
const appStore = useAppStore()
const clientAppStore = useClientAppStore()
const ctrLogger = useCtrStore()
const nativeAppSDK = useNativeAppStore()

const adsStore = useAdsStore()
const adBlockStore = useAdBlockStore()
const loggerStore = useLoggerStore()
const notificationsQueue = useNotificationsQueue()
const router = useRouter()
const route = useRoute()
const pwaStore = usePWAInstallStore()
const pushSubscriptionStore = usePushSubscriptionStore()
const layoutStore = useLayoutStore()
const modalInstallGooglePlayStore = useModalInstallGooglePlayStore()
const modalPlayOnAppStore = useModalPlayOnAppStore()
const googleAuthStore = useGoogleAuthStore()
const { fetchGoogleProvidersEnabled, isGoogleProvidersEnabled } = useGoogleProvidersEnabled()

const { pageStatus, platform: platformFromStore, isGameDistribution } = storeToRefs(appStore)
const isLayoutVisible = computed(() => !route.meta.noLayout)
const noLazyHydration = computed(() => route.meta.noLazyHydration)

function nativeAppUnlockOrientation() {
    if (route.name !== ROUTE_NAMES.EXPORT_GAME_PAGE) {
        nativeAppSDK.postMessage('changeOrientation', { action: 'unlock' })
    }
}
function sendOneSignalAuidToNative() {
    const auid = clientAppStore.auid?.trim()
    if (!auid || !nativeAppSDK.isAvailable) {
        return
    }

    nativeAppSDK.postMessage('oneSignal.event', { auid })
}

function isNewUser(auidTimestamp: number) {
    return Date.now() - auidTimestamp < 1000 * 60 * 60 * 24
}

const userStore = useUserStore()

const manuallyHandledError = computed(() => route.meta.manuallyHandledErrors)

const prepareGoogleAds = computed(() => {
    return !isGameDistribution.value && !adsStore.nativeBannersEnabled
})

useBrowserRumTracking({
    clsThreshold: 0.25,
    onHighCls: ({ cls, viewport, sources }) => {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'cls_high',
            action: 'portal_game',
            dimension1: cls.toFixed(4),
            dimension2: viewport,
            dimension3: sources,
            // tmp dirty to log more details
            adUnit: screen.orientation.type,
            adProvider: String(window.scrollY),
        })
    },
    onTTFB: (ttfb) => {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'page_ttfb',
            dimension1: String(Math.round(ttfb)),
        })
    },
    onLCP: (lcp) => {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'page_lcp',
            dimension1: String(Math.round(lcp)),
        })
    },
})

onServerPrefetch(async () => {
    const ctx = useSSRContext<RenderCacheContext>()

    if (!ctx) {
        return
    }

    const initialQuery = { ...route.query }

    // TODO cleanup
    delete (initialQuery as Record<string, unknown>)[PG_QUERY_START]
    delete (initialQuery as Record<string, unknown>)[PG_QUERY_END]

    const audience = initialQuery[SSR_QUERY.AUDIENCE]
    const initialAudience = Array.isArray(audience) ? audience[0] : audience

    appStore.initialize({
        host: ctx.host,
        initialQuery,
        platform: ctx.platform,
        isNativeIos: ctx.isNativeIos,
        isNativeAndroid: ctx.isNativeAndroid,
        appVersion: ctx.appVersion,
        countryCode: ctx.countryCode,
        clid: ctx.clid,
        experiments: ctx.experiments,
        rankingParams: initialAudience
            ? {
                  audience: initialAudience,
              }
            : {
                  clusterId: ctx.rankingParams.clusterId,
                  audience: ctx.rankingParams.audience,
              },
    })

    if (ctx.pageStatus) {
        appStore.setPageStatus(ctx.pageStatus)
    } else {
        userStore.hasAuthToken = ctx.hasAuthToken
        if (!ctx.hasAuthToken) {
            userStore._isUserInfoLoaded = true
        }
    }

    await fetchGoogleProvidersEnabled()
})

// Doing all app initialization here, no risk for SSR hydration mismatch
// oxlint-disable-next-line playgama/no-on-before-mount
onBeforeMount(() => {
    const clientDevice = parseDevice(window.navigator.userAgent)
    const rankingParamsFromCookie = getRankingParamsFromCookies(document.cookie)
    const rawRankingParams = route.query[SSR_QUERY.AUDIENCE]
        ? {
              audience: route.query[SSR_QUERY.AUDIENCE] as string | undefined,
          }
        : {
              clusterId: rankingParamsFromCookie.clusterId,
              audience: (route.query[SSR_QUERY.AUDIENCE] as string | undefined) || rankingParamsFromCookie.audience,
          }
    const host = appStore.host || window.location.host
    const queryClid = route.query[SSR_QUERY.CLID]
    const landingClid = parseLandingAttributes(getLandingClidFromCookies(document.cookie)).clid

    appStore.initialize({
        host,
        initialQuery: { ...route.query },
        platform: resolvePlatform(
            window.navigator.userAgent,
            getPlatformFromCookies(document.cookie) as PlatformType | undefined,
        ),
        isNativeIos: clientDevice.isNativeIos,
        isNativeAndroid: clientDevice.isNativeAndroid,
        appVersion: clientDevice.appVersion,
        countryCode: appStore.countryCode,
        clid:
            appStore.initialClid ||
            resolveClid({
                host,
                landingClid,
                queryClid: typeof queryClid === 'string' ? queryClid : undefined,
            }),
        experiments: appStore.experimentsRaw || {},
        rankingParams: rawRankingParams,
    })
    updateRankingCookies(rawRankingParams)
    window.dataLayer = window.dataLayer || []
    window.OneSignalDeferred = window.OneSignalDeferred || []
    clientAppStore.setEffectiveType()
    loggerStore.setCTRSampling()
    nativeAppUnlockOrientation()
    // log initial navigation with current url
    loggerStore.logEvent({
        event: 'user_time',
        eventName: 'navigation',
        dimension1: window.location.href,
    })

    // beforeEach to do work before next page mounts
    router.beforeEach((to) => {
        appStore.setPageStatus(200)
        // reset content_category and provider for upcoming page to avoid misleading targeting data
        const newAdTargeting = {
            content_category: '',
            provider: '',
            game_id: '',
            hru: to.name && ROUTES_WITH_GAME_PARAM.includes(to.name as string) ? (to.params.game as string) : '',
            page: to.name as string,
            effectiveType: clientAppStore.effectiveType as string,
        }

        adsStore.updateTargeting(newAdTargeting)
        nativeAppUnlockOrientation()
    })

    router.afterEach(() => {
        // log following navigations
        loggerStore.logEvent({
            event: 'user_time',
            eventName: 'navigation',
            dimension1: window.location.href,
        })
    })

    window.addEventListener('beforeunload', () => {
        loggerStore.logEvent({
            event: 'user_time',
            eventName: 'portal_session_end',
        })
    })

    // dev-mode store initialization
    clientAppStore.setUserAgent(window.navigator.userAgent)
    clientAppStore.setAuidInfo(getAUID(document.cookie))
    clientAppStore.setReferer(document.referrer)

    // TODO Dev mode runs without SSR, where experiments are normally assigned

    // TODO SSR
    clientAppStore.setUserParams({
        userAcquisitionParams: getUACFromCookies30d(document.cookie),
    })
    clientAppStore.updateLandingAttributes()

    const GAME_DISTRIBUTION_GAME_ID = route.query[CLIENT_QUERY.GAME_DISTRIBUTION_GAME_ID]

    adsStore.setAdServiceConfig({
        // TODO make updateTargeting work with initial before/afterRoute
        targeting: {
            hru:
                (route.name &&
                    ROUTES_WITH_GAME_PARAM.includes(route.name as string) &&
                    (route.params.game as string)) ||
                '',
            page: route.name as string,
            effectiveType: clientAppStore.effectiveType as string,
            cohort: clientAppStore.cohort || '',
            rankingAudience: `${appStore.rankingParams?.audience || 'NOT_SET'}-${appStore.platform}`,
        },
        auid: clientAppStore.auid || '',
        isGoogleProvidersEnabled: isGoogleProvidersEnabled.value,
        gameDistributionGameId: GAME_DISTRIBUTION_GAME_ID ? (GAME_DISTRIBUTION_GAME_ID as string) : undefined,
    })
    adsStore.consentManager?.ready.then(() => {
        adBlockStore.detectAdBlockStatus(loggerStore.logEvent)
    })

    window.dataLayer.push({
        device_platform: platformFromStore.value,
        new_user: isNewUser(clientAppStore.auidTimestamp),
        auid: clientAppStore.auid,
    })

    let hydrationProblemReported = false
    installVueConsoleHandlers((type, msg) => {
        // log only first hydration related warning
        if (msg.toLowerCase().includes('hydration')) {
            if (hydrationProblemReported) {
                return
            }
            hydrationProblemReported = true
        }
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'vue_message',
            dimension1: type,
            dimension2: msg,
        })
    })
})

const adBlockDetectionIntervalId = ref<number>()

const { user } = storeToRefs(userStore)
watch(
    // if watching user.value it will trigger every time we re-fetch user and it gets re-assigned
    // but watching ._id we actually make it trigger when user login/logoff
    () => user.value?._id,
    async () => {
        if (user.value) {
            clientAppStore.setAuidInfo(getAUID(document.cookie))

            window.dataLayer.push({
                user_id: user.value._id,
                new_user: isNewUser(clientAppStore.auidTimestamp),
                auid: clientAppStore.auid,
                user_email: user.value.email,
                user_email_sha256: user.value.email ? await sha256hex(user.value.email) : undefined,
            })
        }

        googleAuthStore.maybePromoteNativeLogin()
        adsStore.updateTargeting({
            [TargetingMapping.IS_LOGGED_IN]: user.value ? '1' : '0',
        })
    },
)

watch(
    () => adsStore.consentUiState,
    (state, previousState) => {
        if (state === 'hidden') {
            notificationsQueue.init()
        } else if (state === 'visible' && previousState === 'hidden') {
            // while the state is still 'unknown' notifications keep queueing up, and dispose() would
            // drop them for the whole session - only tear the queue down once it was actually running
            notificationsQueue.dispose()
        }
    },
)

watch(
    () => clientAppStore.auid,
    () => {
        sendOneSignalAuidToNative()
    },
)

function onVisibilityChanged() {
    layoutStore.setTabFocused(document.visibilityState === 'visible')
}

function onOrientationChange() {
    layoutStore.setOrientation(screen.orientation.type)
    loggerStore.logInterfaceEvents({
        eventName: 'interface_rotate',
    })
}

onMounted(async () => {
    pwaStore.init()
    pushSubscriptionStore.init()
    nativeAppSDK.init()
    sendOneSignalAuidToNative()
    clientAppStore.initPageLoadProgressTracking()

    // this enables css :active pseudo-class on touch devices
    const noop = () => {}
    document.body.addEventListener('touchstart', noop, false)

    document.addEventListener('visibilitychange', onVisibilityChanged)

    layoutStore.setOrientation(screen.orientation.type)
    screen.orientation.addEventListener('change', onOrientationChange)
    modalInstallGooglePlayStore.init()
    if (!appStore.isOppoWL) {
        modalPlayOnAppStore.init()
    }

    await userStore.getUser()
    // only once the user is known - before that isAuthorized is false for everyone
    googleAuthStore.maybePromoteNativeLogin()
    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'portal_session_start',
        dimension3: clientAppStore.effectiveType as string,
    })
})

onUnmounted(() => {
    if (adBlockDetectionIntervalId.value) {
        clearInterval(adBlockDetectionIntervalId.value)
    }
    modalInstallGooglePlayStore.dispose()
    modalPlayOnAppStore.dispose()
    googleAuthStore.dispose()
    ctrLogger.disconnect()
    nativeAppSDK.dispose()

    document.removeEventListener('visibilitychange', onVisibilityChanged)
    screen.orientation.removeEventListener('change', onOrientationChange)
})
</script>
